import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../core/_base/crud';
import {
  Doctor,
  DoctorOnServerDeleted,
  DoctorOnServerUpdated,
  DoctorsDataSource,
  DoctorService,
  DoctorsPageRequested,
  DoctorStatusOnServerUpdated,
  selectDoctorsActionLoading,
  selectErrorState,
  ViewContext,
} from '../../../../../../core/lists';
import { TabletUtil } from '../../../../../../core/utils';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/core/auth/_services';
import { Update } from '@ngrx/entity';
import { OfficesService } from '../../../../../../core/management';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { FilteredViewService } from '../../../../../../core/services/filtered-view.service';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { utc } from 'moment';
import { NgxPermissionsService } from 'ngx-permissions';
import { ZoomInEntityComponent } from '../../../../../partials/content/general/zoom-in-entity/zoom-in-entity.component';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { DownloadService } from '../../../../../../shared/utils/download.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-doctors-list',
  templateUrl: './doctors-list.component.html',
  styleUrls: ['./doctors-list.component.scss'],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoctorsListComponent implements OnInit, OnDestroy {
  dataSource: DoctorsDataSource;
  displayedColumns = [
    'id',
    'profile',
    'name',
    'npi',
    'sln',
    'officeName',
    'officePhone',
    'fax',
    'isActive',
    'actions',
  ];
  searchBarColumns = [
    'searchId',
    'searchProfile',
    'searchNameEmail',
    'searchNPI',
    'searchSLN',
    'searchOfficeName',
    'searchOfficePhone',
    'searchFax',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchIdInput = '';
  searchNameEmailInput = '';
  searchNPIInput = '';
  searchSLNInput = '';
  searchOfficePhoneInput = '';
  searchFaxInput = '';
  searchIsActiveSelect = 'true';
  @ViewChild('searchOfficeAutocomplete') searchOfficeAutocomplete: GeneralAutocompleteComponent;
  getOfficeAutocomplete = this.officesService.searchOffices.bind(this.officesService);
  statusTracker = 0;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize;

  constructor(
    private navigationService: NavigationService,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private tabletUtil: TabletUtil,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private authService: AuthService,
    private officesService: OfficesService,
    private fb: FormBuilder,
    private sessionService: FilteredViewService,
    private doctorService: DoctorService,
    private permissionsService: NgxPermissionsService,
    public stringUtils: StringUtilsService,
    private downloadService: DownloadService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'name';
      this.sort.direction = this.sort.direction || 'asc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadDoctorsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadDoctorsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new DoctorsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe();
    this.subscriptions.push({ id: 4, sub: entitiesSubscription });
    this.searchForm = this.fb.group({
      officeControl: [],
      search: [],
      status: ['true'],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;

      if (this.filtersApplied.officeId) {
        const officeFilterSubscription = this.officesService
          .getOfficeById(this.filtersApplied.officeId)
          .subscribe((office) => {
            this.searchOfficeAutocomplete.value = { id: office.id, name: office.name };
            this.searchOfficeAutocomplete.setValue({ id: office.id, name: office.name });
            this.loadDoctorsList();
          });
        this.subscriptions.push({ id: 0, sub: officeFilterSubscription });
        return;
      }
    }
    // Read from URL itemId, for restore previous state
    // Comment by Nourhan: please don't change the order of this scope
    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadDoctorsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadDoctorsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'name',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new DoctorsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchIdInput = appliedFilters.id;
        this.searchNameEmailInput = appliedFilters.nameEmail;
        this.searchNPIInput = appliedFilters.npi;
        this.searchSLNInput = appliedFilters.sln;
        this.searchOfficePhoneInput = appliedFilters.officePhone?.replace(/[()\-\s]/g, '');
        this.searchFaxInput = appliedFilters.fax?.replace(/[()\-\s]/g, '');
        if (this.searchOfficeAutocomplete)
          this.searchOfficeAutocomplete.value.id = appliedFilters.officeId;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'asc';
      this.sort.active = this.filtersApplied?.sortActive || 'name';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.id = this.searchIdInput;
      filter.nameEmail = this.searchNameEmailInput;
      filter.npi = this.searchNPIInput;
      filter.sln = this.searchSLNInput;
      filter.officePhone = this.searchOfficePhoneInput?.replace(/[()\-\s]/g, '');
      filter.fax = this.searchFaxInput?.replace(/[()\-\s]/g, '');
      filter.officeId = this.searchOfficeAutocomplete?.value?.id;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'asc';
    filter.sortActive = this.sort.active || 'name';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }
  deleteDoctor(_item: Doctor) {
    const _title: string = this.translate.instant('DOCTOR.DELETE_DOCTOR_SIMPLE.TITLE');
    const _description: string = this.translate.instant('DOCTOR.DELETE_DOCTOR_SIMPLE.DESCRIPTION');
    const _waitDesciption: string = this.translate.instant(
      'DOCTOR.DELETE_DOCTOR_SIMPLE.WAIT_DESCRIPTION',
    );
    const _deleteMessage = this.translate.instant('DOCTOR.DELETE_DOCTOR_SIMPLE.MESSAGE');

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }

      this.store.dispatch(new DoctorOnServerDeleted({ id: _item.id }));
      const actionLoadingSubscription = this.store
        .pipe(select(selectDoctorsActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error && error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            if (this.subscriptions.find((sub) => sub.id === 9) === undefined) {
              this.subscriptions.push({ id: 9, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.error);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Delete,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _deleteMessage,
                MessageType.Delete,
                3000,
                false,
                false,
              );
              this.loadDoctorsList();
            }
          }
          this.cdr.detectChanges();
        });
      if (this.subscriptions.find((sub) => sub.id === 8) === undefined) {
        this.subscriptions.push({ id: 8, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 7) === undefined) {
      this.subscriptions.push({ id: 7, sub: dialogRefSubscription });
    }
  }

  updateSingleStatus(doctor: Doctor) {
    const _title: string = this.translate.instant('DOCTOR.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant('DOCTOR.UPDATE_STATUS_SIMPLE.DESCRIPTION');
    const _waitDescription: string = this.translate.instant(
      'DOCTOR.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );
    const _updateStatusSingleMessage = this.translate.instant(
      'DOCTOR.UPDATE_STATUS_SIMPLE.MESSAGE',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newDoctor: Doctor = Object.assign(new Doctor(), doctor);
      newDoctor.is_active = !newDoctor.is_active;
      const accessObj = {
        entity_id: newDoctor.id,
        access_from: 'Doctor List',
        entity_type: 'DOCTOR',
      };
      this.sessionService.createRecentSession(accessObj).subscribe();
      this.store.dispatch(
        new DoctorStatusOnServerUpdated({
          id: newDoctor.id,
          is_active: newDoctor.is_active
        }),
      );
      const actionLoadingSubscription = this.store
        .pipe(select(selectDoctorsActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error && error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            if (this.subscriptions.find((sub) => sub.id === 15) === undefined) {
              this.subscriptions.push({ id: 15, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.error);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Update,
                3000,
                false,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _updateStatusSingleMessage,
                MessageType.Update,
                3000,
                false,
                false,
              );
              this.loadDoctorsList();
            }
          }
          this.cdr.detectChanges();
        });
      if (this.subscriptions.find((sub) => sub.id === 14) === undefined) {
        this.subscriptions.push({ id: 14, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 13) === undefined) {
      this.subscriptions.push({ id: 13, sub: dialogRefSubscription });
    }
  }

  canViewDoctor(): boolean {
    return !!this.permissionsService.getPermission('canViewDoctor');
  }
  canEditDoctor(): boolean {
    return !!this.permissionsService.getPermission('canEditDoctor');
  }
  addDoctor() {
    this.navigationService.navigateTo('lists/doctors/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewDoctor(doctor: Doctor) {
    if (!this.canViewDoctor()) return;

    if (this.canEditDoctor()) {
      this.editDoctor(doctor.id);
      return;
    }
    const queryParams = {
      viewContext: ViewContext.doctors,
    };
    this.navigationService.navigateTo('lists/doctors/view/' + doctor.id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewDoctorDblclick(ref: string, doctor: Doctor): void {
    this.tabletUtil.handleDblclick(ref, this.viewDoctor.bind(this, doctor));
  }

  editDoctor(id) {
    const queryParams = {
      viewContext: ViewContext.doctors,
    };
    const accessObj = {
      entity_id: id,
      access_from: 'Doctor List',
      entity_type: 'DOCTOR',
    };
    this.sessionService.createRecentSession(accessObj).subscribe();
    this.navigationService.navigateTo('lists/doctors/edit/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  formatOfficeOption(office: { id: number; name: string }): string {
    return office?.name || '';
  }

  reloadDoctorsList(): void {
    this.paginator.pageIndex = 0;

    this.loadDoctorsList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadDoctorsList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput = '';
    this.searchNameEmailInput = '';
    this.searchNPIInput = '';
    this.searchSLNInput = '';
    this.searchOfficePhoneInput = '';
    this.searchFaxInput = '';
    this.searchOfficeAutocomplete?.setValue(null);
    this.searchOfficeAutocomplete?.updateValueAndValidity();
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadDoctorsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadDoctorsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadDoctorsList();
  }

  exportDoctorsAsCSV(event: Event): void {
    event.stopPropagation();

    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'asc',
      this.sort.active || 'name',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.doctorService
      .getDoctorsAsCSV(queryParams)
      .pipe(take(1))
      .subscribe((res) => {
        const filename = `Doctors List [${utc(new Date())
          .local()
          .format('YYYY-MM-DD, hh.mm a')}].csv`;
        this.downloadService.downloadFile(res, filename, 'text/csv');
      });
  }

  showDoctorPhoto(doctor) {
    if (!doctor.pre_signed_url?.url) {
      return;
    }
    this.dialog.open(ZoomInEntityComponent, { data: { entity: doctor }, maxWidth: '600px' });
  }
}
